// src/pages/FrmAnularIngreso.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Table, Typography, Card, Button, Space, Popconfirm, Modal, Grid } from "antd";
import axios from "axios";
import useCurrentUser from "../hooks/useCurrentUser"; // ajusta si tu ruta difiere

const { Title } = Typography;
const { useBreakpoint } = Grid;

// .env
// REACT_APP_API_URL=https://api.migranja.vetacloud.co/api
// REACT_APP_PAGE_SIZE_INGRESOS=5
// REACT_APP_PAGE_SIZE_DETALLE=5
const API_BASE = process.env.REACT_APP_API_URL || "https://localhost:7118/api";
const PAGE_SIZE_INGRESOS = parseInt(process.env.REACT_APP_PAGE_SIZE_INGRESOS || "5", 10);
const PAGE_SIZE_DETALLE = parseInt(process.env.REACT_APP_PAGE_SIZE_DETALLE || "5", 10);

// Helpers de normalización ($values, PascalCase)
function extractItems(data) {
  if (!data) return [];
  const candidate = data.items ?? data.Items ?? data;
  if (Array.isArray(candidate)) return candidate;
  if (candidate && Array.isArray(candidate.$values)) return candidate.$values;
  return [];
}
function extractTotal(data, itemsLength = 0) {
  if (!data) return 0;
  if (typeof data.total === "number") return data.total;
  if (typeof data.Total === "number") return data.Total;
  return itemsLength;
}
function fmtNumber(val, digits = 2) {
  if (val === null || val === undefined) return "";
  const n = typeof val === "number" ? val : Number(val);
  if (Number.isNaN(n)) return String(val);
  return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

// ====== Conversión / Validación visual ======
const EPS_RATIO = 0.0001;

// ratio = (cantidadActual base) / (cantidad unidad) / (factorBaseUnidad)  → ideal ≈ 1
function computeRatioBase(cantidadActual, cantidadUnidad, factorBaseUnidad) {
  const ca = cantidadActual == null ? null : Number(cantidadActual);
  const cu = cantidadUnidad == null ? null : Number(cantidadUnidad);
  const fb = factorBaseUnidad == null ? null : Number(factorBaseUnidad);
  if (ca == null || cu == null || fb == null) return null;
  if (!Number.isFinite(ca) || !Number.isFinite(cu) || !Number.isFinite(fb)) return null;
  if (cu === 0 || fb === 0) return null;
  const r = ca / cu / fb;
  return Number.isFinite(r) ? r : null;
}

const FrmAnularIngreso = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md; // xs/sm -> móvil
  const { currentUser, isAuthenticated } = useCurrentUser();

  // Ingresos
  const [ingresos, setIngresos] = useState([]);
  const [ingLoading, setIngLoading] = useState(false);
  const [ingPage, setIngPage] = useState(1);
  const [ingTotal, setIngTotal] = useState(0);

  // Selección
  const [selected, setSelected] = useState(null);

  // Detalle
  const [detalles, setDetalles] = useState([]);
  const [detLoading, setDetLoading] = useState(false);
  const [detPage, setDetPage] = useState(1);
  const [detTotal, setDetTotal] = useState(0);

  // Estado de anulación para deshabilitar el botón
  const [anulando, setAnulando] = useState(false);

  // URLs
  const urlIngresos = useMemo(
    () => `${API_BASE}/Entrada/activos?page=${ingPage}&pageSize=${PAGE_SIZE_INGRESOS}`,
    [ingPage]
  );
  const urlDetalle = useMemo(() => {
    if (!selected?.id) return null;
    return `${API_BASE}/DetalleEntrada/por-entrada-con-unidad/${selected.id}?page=${detPage}&pageSize=${PAGE_SIZE_DETALLE}`;
  }, [selected, detPage]);

  // Cargar Ingresos
  const fetchIngresos = async () => {
    try {
      setIngLoading(true);
      const res = await axios.get(urlIngresos);
      const items = extractItems(res.data).map((x) => ({
        id: x.id ?? x.Id,
        nombre: x.nombre ?? x.Nombre,
        fechaEntrada: x.fechaEntrada ?? x.FechaEntrada,
        docEntrada: x.docEntrada ?? x.DocEntrada,
        total: x.total ?? x.Total,
      }));
      setIngresos(items);
      setIngTotal(extractTotal(res.data, items.length));
      if (selected && !items.some((r) => r.id === selected.id)) {
        setSelected(null);
        setDetalles([]);
        setDetPage(1);
        setDetTotal(0);
      }
    } catch (err) {
      console.error(err);
      Modal.error({ title: "Error", content: "No se pudieron cargar los ingresos." });
    } finally {
      setIngLoading(false);
    }
  };

  // Cargar Detalle (solo la página visible)
  const fetchDetalle = async () => {
    if (!urlDetalle) return;
    try {
      setDetLoading(true);
      const res = await axios.get(urlDetalle);
      const items = extractItems(res.data).map((x) => {
        const unidadNombre = x.unidadNombre ?? x.UnidadNombre ?? null;
        const unidadAbreviatura = x.unidadAbreviatura ?? x.UnidadAbreviatura ?? null;
        const factorBaseUnidad = x.factorBaseUnidad ?? x.FactorBaseUnidad ?? null;

        const cantidad = x.cantidad ?? x.Cantidad;                   // unidad (tal cual JSON)
        const cantidadActual = x.cantidadActual ?? x.CantidadActual; // base

        const baseRatio = computeRatioBase(cantidadActual, cantidad, factorBaseUnidad);

        return {
          id: x.id ?? x.Id,
          idEntrada: x.idEntrada ?? x.IdEntrada,
          idProducto: x.idProducto ?? x.IdProducto,
          producto: x.producto ?? x.Producto,
          cantidad,
          cantidadActual,
          precioUnitario: x.precioUnitario ?? x.PrecioUnitario,
          valorCompra: x.valorCompra ?? x.ValorCompra,
          vencimiento: x.vencimiento ?? x.Vencimiento,
          idUnidadMedida: x.idUnidadMedida ?? x.IdUnidadMedida,

          // Unidad
          unidadNombre,
          unidadAbreviatura,
          factorBaseUnidad,
          unidadTexto: unidadAbreviatura || unidadNombre || "",

          // Ratio mostrado en "Cant. Actual (base)"
          baseRatio,
        };
      });

      setDetalles(items);
      setDetTotal(extractTotal(res.data, items.length));
    } catch (err) {
      console.error(err);
      Modal.error({ title: "Error", content: "No se pudo cargar el detalle." });
    } finally {
      setDetLoading(false);
    }
  };

  useEffect(() => {
    fetchIngresos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlIngresos]);

  useEffect(() => {
    if (selected?.id) fetchDetalle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlDetalle]);

  // ======== Utilidades Inventario ========
  const fetchAllDetallesEntrada = async (idEntrada) => {
    const pageSize = 200;
    let page = 1;
    let total = 0;
    let acc = [];

    while (true) {
      const url = `${API_BASE}/DetalleEntrada/por-entrada-con-unidad/${idEntrada}?page=${page}&pageSize=${pageSize}`;
      const res = await axios.get(url);
      const items = extractItems(res.data).map((x) => ({
        idProducto: x.idProducto ?? x.IdProducto,
        cantidadActual: x.cantidadActual ?? x.CantidadActual, // base
      }));
      acc = acc.concat(items);
      total = extractTotal(res.data, items.length);
      if (acc.length >= total || items.length === 0) break;
      page += 1;
    }
    return acc;
  };

  const getInventarioCantidad = async (idProducto) => {
    const url = `${API_BASE}/Inventario/producto/${idProducto}`;
    const { data } = await axios.get(url);
    // cantidad
      const raw = data?.cantidad ?? data?.Cantidad ?? data;
      const cantidad = Number(raw);
      const cantidadNum = Number.isFinite(cantidad) ? cantidad : 0;

      // id inventario (si viene)
      const inventarioId = data?.id ?? data?.Id ?? null;

      return { inventarioId, cantidad: cantidadNum };
  };

  const putInventarioCantidad = async (idProducto, cantidadNueva) => {
    const url = `${API_BASE}/Inventario/producto/${idProducto}/cantidad`;
    const safeCantidad = Math.max(0, Number(cantidadNueva) || 0);
    await axios.put(url, { cantidad: safeCantidad });
  };
  // ======== Utilidades Plan de Consumo ========

    // GET /DetallePlanConsumo/ultimo?idInventario={inventarioId}&estado=0
    const getUltimoDetallePlanConsumo = async (inventarioId) => {
      const url = `${API_BASE}/DetallePlanConsumo/ultimo?idInventario=${inventarioId}&estado=0`;
      const { data } = await axios.get(url);
      const detalleId = data?.id ?? data?.Id ?? null;
      const idInventario = data?.idInventario ?? data?.IdInventario ?? inventarioId ?? null;
      return { detalleId, idInventario };
    };

    // PUT /DetallePlanConsumo/estado-cero-por-inventario/{inventarioId}
    const putEstadoCeroPorInventario = async (inventarioId) => {
      const url = `${API_BASE}/DetallePlanConsumo/estado-cero-por-inventario/${inventarioId}`;
      await axios.put(url);
    };

    // PUT /DetallePlanConsumo/{id}/activar
    const putActivarDetallePlanConsumo = async (detalleId) => {
      const url = `${API_BASE}/DetallePlanConsumo/${detalleId}/activar`;
      await axios.put(url);
    };

  // ======== Acción: Anular ingreso + ajustar inventario ========
  const anularIngreso = async () => {
    if (!selected?.id) {
      Modal.warning({ title: "Selecciona un ingreso", content: "Primero elige un ingreso de la lista." });
      return;
    }
    if (!isAuthenticated || !currentUser?.usuarioId) {
      Modal.warning({
        title: "Usuario no identificado",
        content: "No fue posible obtener el usuario actual. Inicia sesión e inténtalo de nuevo.",
      });
      return;
    }

    setAnulando(true);
    try {
      // 1) Anular la entrada
      const putUrl = `${API_BASE}/Entrada/${selected.id}/estado`;
      await axios.put(putUrl, { estado: 2, usuarioAnula: currentUser.usuarioId });

      // 2) Ajustar inventario ítem por ítem
      const detallesAll = await fetchAllDetallesEntrada(selected.id);
      const errores = [];
const advertenciasPlan = [];

for (const det of detallesAll) {
  const idProd = det.idProducto;
  const cantActual = Number(det.cantidadActual) || 0; // base

  try {
    // --- Inventario: resta base ---
    const { inventarioId, cantidad: invCantidad } = await getInventarioCantidad(idProd);
    const nuevaCantidad = invCantidad - cantActual;
    await putInventarioCantidad(idProd, nuevaCantidad);

    // --- Plan de Consumo (si hay inventarioId válido) ---
    if (inventarioId != null) {
      try {
        // 1) último detalle con estado=0
        const { detalleId } = await getUltimoDetallePlanConsumo(inventarioId);

        // 2) poner estado=0 por inventario
        await putEstadoCeroPorInventario(inventarioId);

        // 3) activar el detalle obtenido (si existe)
        if (detalleId != null) {
          await putActivarDetallePlanConsumo(detalleId);
        } else {
          advertenciasPlan.push({ inventarioId, motivo: "GET último no devolvió id de detalle" });
        }
      } catch (pcErr) {
        console.error("PlanConsumo fallo para inventario", inventarioId, pcErr);
        advertenciasPlan.push({ inventarioId, motivo: "Error en plan de consumo" });
      }
    } else {
      advertenciasPlan.push({ inventarioId: null, motivo: `Sin inventarioId para producto ${idProd}` });
    }

  } catch (e) {
    console.error("Error ajustando inventario para producto", idProd, e);
    errores.push(idProd);
  }
}


      if (errores.length === 0) {
        Modal.success({ title: "Ingreso anulado", content: "Ingreso anulado satisfactoriamente." });
      } else {
        Modal.warning({
          title: "Ingreso anulado con advertencias",
          content: `No se pudo ajustar inventario para: ${errores.join(", ")}.`,
          width: 600,
        });
      }

      await fetchIngresos();
      setSelected(null);
      setDetalles([]);
      setDetPage(1);
      setDetTotal(0);
    } catch (err) {
      console.error(err);
      Modal.error({ title: "Error", content: "No fue posible anular el ingreso y ajustar el inventario." });
    } finally {
      setAnulando(false);
    }
  };

  // ========== Columnas responsivas ==========
  const getIngresosColumns = (mobile) => {
    if (mobile) {
      return [
        { title: "Nombre", dataIndex: "nombre", key: "nombre", ellipsis: true, width: 180 },
        { title: "Fecha", dataIndex: "fechaEntrada", key: "fecha", width: 160, render: (v) => (v ? new Date(v).toLocaleString() : "") },
        { title: "Total", dataIndex: "total", key: "total", align: "right", width: 120, render: (v) => fmtNumber(v, 2) },
      ];
    }
    return [
      { title: "Nombre", dataIndex: "nombre", key: "nombre", ellipsis: true },
      { title: "FechaEntrada", dataIndex: "fechaEntrada", key: "fechaEntrada", render: (v) => (v ? new Date(v).toLocaleString() : "") },
      { title: "DocEntrada", dataIndex: "docEntrada", key: "docEntrada", ellipsis: true },
      { title: "Total", dataIndex: "total", key: "total", align: "right", render: (v) => fmtNumber(v, 2) },
    ];
  };

  const getDetalleColumns = (mobile) => {
    if (mobile) {
      return [
        { title: "Prod.", dataIndex: "producto", key: "producto", ellipsis: true, width: 160 },
        {
          title: "Cant. (und)",
          dataIndex: "cantidad",
          key: "cantidadUnidad",
          align: "right",
          width: 110,
          render: (v) => (v == null ? "" : fmtNumber(v, 0)), // ENTERO
        },
        {
          title: "Base",
          dataIndex: "baseRatio",
          key: "baseRatio",
          align: "right",
          width: 90,
          render: (v) => {
            if (v == null) return "";
            const txt = fmtNumber(v, 0); // ENTERO
            const mismatch = Math.abs(v - 1) > EPS_RATIO;
            return mismatch ? <span style={{ color: "#d9363e", fontWeight: 600 }}>{txt}</span> : txt;
          },
        },
        { title: "Und.", dataIndex: "unidadTexto", key: "unidadTexto", width: 80, render: (v) => v || "" },
      ];
    }
    return [
      { title: "Producto", dataIndex: "producto", key: "producto", ellipsis: true },
      {
        title: "Cantidad (unidad)",
        dataIndex: "cantidad",
        key: "cantidadUnidad",
        align: "right",
        render: (v) => (v == null ? "" : fmtNumber(v, 0)), // ENTERO
      },
      {
        title: "Cant. Actual (base)",
        dataIndex: "baseRatio",
        key: "baseRatio",
        align: "right",
        render: (v) => {
          if (v == null) return "";
          const txt = fmtNumber(v, 0); // ENTERO
          const mismatch = Math.abs(v - 1) > EPS_RATIO;
          return mismatch ? <span style={{ color: "#d9363e", fontWeight: 600 }}>{txt}</span> : txt;
        },
      },
      { title: "Unidad", dataIndex: "unidadTexto", key: "unidadTexto", render: (v) => v || "" },
      {
        title: "Precio Unit.",
        dataIndex: "precioUnitario",
        key: "precioUnitario",
        align: "right",
        render: (v) => (v == null ? "" : fmtNumber(v, 0)),
      },
      {
        title: "Valor Compra",
        dataIndex: "valorCompra",
        key: "valorCompra",
        align: "right",
        render: (v) => (v == null ? "" : fmtNumber(v, 0)),
      },
      {
        title: "Vence",
        dataIndex: "vencimiento",
        key: "vencimiento",
        render: (v) => (v ? new Date(v).toLocaleDateString() : ""),
      },
    ];
  };

  return (
    <div style={{ padding: 16 }}>
      {/* estilos para móvil: encabezados sin wrap y fila seleccionada */}
      <style>{`
        .selected-row { background-color: #e6f4ff !important; }
        .selected-row:hover > td { background-color: #d0e8ff !important; }
        .ant-table-thead > tr > th { white-space: nowrap; }
      `}</style>

      <Title level={2} style={{ marginBottom: 16 }}>Ingresos</Title>

      <Card>
        <Table
          columns={getIngresosColumns(isMobile)}
          dataSource={ingresos}
          loading={ingLoading}
          size={isMobile ? "small" : "middle"}
          sticky={isMobile}
          scroll={isMobile ? { x: "max-content" } : undefined}
          pagination={{
            current: ingPage,
            pageSize: PAGE_SIZE_INGRESOS,
            total: ingTotal,
            showSizeChanger: false,
            simple: isMobile,
          }}
          onChange={(p) => setIngPage(p.current)}
          rowKey={(r) => r.id}
          rowClassName={(record) => (record.id === selected?.id ? "selected-row" : "")}
          onRow={(record) => ({
            onClick: () => {
              setSelected(record);
              setDetPage(1);
            },
            style: { cursor: "pointer" },
          })}
        />
      </Card>

      <Space
        direction={isMobile ? "vertical" : "horizontal"}
        align="center"
        style={{ marginTop: 24, marginBottom: 8, width: "100%", display: "flex", justifyContent: "space-between" }}
      >
        <Title level={4} style={{ margin: 0 }}>Detalle</Title>

        <Popconfirm
          title="Anular ingreso"
          description={selected?.id ? `¿Seguro que deseas anular el ingreso #${selected.id}?` : "Selecciona un ingreso primero."}
          onConfirm={anularIngreso}
          okText="Sí, anular"
          cancelText="Cancelar"
          disabled={!selected?.id || anulando}
          placement={isMobile ? "top" : "topRight"}
        >
          <Button type="primary" danger disabled={!selected?.id || anulando} loading={anulando} block={isMobile}>
            Anular ingreso
          </Button>
        </Popconfirm>
      </Space>

      <Card>
        <Table
          columns={getDetalleColumns(isMobile)}
          dataSource={detalles}
          loading={detLoading}
          size={isMobile ? "small" : "middle"}
          sticky={isMobile}
          scroll={isMobile ? { x: "max-content" } : undefined}
          pagination={{
            current: detPage,
            pageSize: PAGE_SIZE_DETALLE,
            total: detTotal,
            showSizeChanger: false,
            simple: isMobile,
          }}
          onChange={(p) => setDetPage(p.current)}
          rowKey={(r) => r.id}
          locale={{
            emptyText: selected
              ? "Aquí se mostrará el detalle del ingreso seleccionado."
              : "Selecciona un ingreso para ver el detalle aquí.",
          }}
        />
      </Card>
    </div>
  );
};

export default FrmAnularIngreso;
